import _ from 'lodash';
import MegogoPlayer from 'mggp-player';
import * as Cookie from 'tiny-cookie';
import initSkin from './initSkin';
import XHRLoader from './xhr-loader';
import ModuleManager from './module-manager';
import makeConfig from './config';
import BinfoToStream from './binfo-to-stream';
import utils from './utils';
import listeners from './player-listeners';
import { UserSettingsStorage } from './UserSettingsStorage';
import { checkGDPR } from './utils/gdpr';
// import MobileUtils from './mobile-utils'; // TODO need fix wstat fullscreen mode
import {
  BROWSER_AUTOPLAY_ABILITY,
  checkBrowserAutoplay,
  checkStreamAutoplay,
} from './autoplayCheck';

document.documentElement.style.setProperty(
  '--interactive-layer-events',
  'none'
);
document.documentElement.style.setProperty(
  '--interactive-layer-zindex',
  'initial'
);

// eslint-disable-next-line no-unused-vars
const __svg__ = {
  path: 'assets/icons/*.svg',
  name: 'sprite.svg',
};

utils.polyfills();

export const BROWSER_AUTOPLAY_STATE = {
  IDLE: 'idle',
  AVAILABLE: 'available',
  AVAILABLE_FORBIDDEN: 'availableForbidden', // when autoplay available but forbidden by get params or stream.autplay value
  NEED_TO_INIT_VIDEOS: 'needToInitVideos',
};

export const AD_GLOBAL_TIMEOUTS = 'ad:globalTimeouts';

export default class MggpLoader {
  //fs fs
  constructor() {
    this.player = new MegogoPlayer();
    this.binfoConverter = new BinfoToStream();
    this.xhrLoader = this.getDefaultLoader();
    this.videoURL = null;
    listeners.init(this);
    this.config = null;
    this.storage = null;
    this.videosInited = false;
    this._getVirtualStream = Function.prototype;
    this.neededScripts = [];

    this.messageMode = null;
    this.videoInfoDeferredData = null; // arguments for videoInfo
    this.deferredPlayerOptions = null;
    this.browserAutoplayResolved = false;
    this.defaultErrorMessage = 'Error player playing, please retry';

    this.player.registerProperty(
      'browserAutoplayState',
      BROWSER_AUTOPLAY_STATE.IDLE,
      true
    );
    this.player.registerProperty('isMobile', false, true);
    this.player.registerProperty('interactive:zone', [], true);
    this.player.registerProperty('objectAudioId', null, true);
    this.player.registerProperty(AD_GLOBAL_TIMEOUTS, null, true);

    try {
      const timeoutsStr = window.localStorage.getItem(AD_GLOBAL_TIMEOUTS);
      if (timeoutsStr) {
        const timeouts = JSON.parse(timeoutsStr);
        this.player.set(AD_GLOBAL_TIMEOUTS, timeouts);
      }
    } catch (error) {
      console.error(error);
    }
  }

  onPlayerContainerClick = (e) => {
    if (e && e.which === 3) return; //MHP-1379 disable right click
    const getVideos = this.player.get('videoInstances');
    const getAutoplay = this.player.get('autoplay');

    if (getVideos && getVideos.length > 0) {
      this.player.off('property:videoInstances', this.onPlayerContainerClick);

      // always initialize videos
      utils.initializeVideos(getVideos, this.player);

      this.config.container.removeEventListener(
        'mousedown',
        this.onPlayerContainerClick,
        true
      );
      this.videosInited = true;
      this.browserAutoplay = true;
      this.player.set('browserAutoplayState', BROWSER_AUTOPLAY_STATE.AVAILABLE);
      this.browserAutoplayResolved = true;
      if (this.videoInfoDeferredData && !this.messageMode) {
        this.onVideoInfo(this.videoInfoDeferredData);
        this.videoInfoDeferredData = null;
      } else if (getAutoplay === false) {
        this.player.set('autoplay', true);
      }
    } else {
      this.player.on('property:videoInstances', this.onPlayerContainerClick);
    }
  };

  getDefaultLoader() {
    return new XHRLoader();
  }

  createKibanaData(data, player) {
    return utils.createKibanaData(data, player);
  }

  _getStream = (url, callback) => {
    const { supportedDrmType } = this.config;
    if (this.config.supportedDrmType && url.indexOf) {
      url =
        url.indexOf('?') !== -1
          ? `${url}&drm_type=${supportedDrmType}`
          : `${url}?drm_type=${supportedDrmType}`;
    }

    this.xhrLoader.load(url, callback || this.onVideoInfo, (message) => {
      const error = {
        name: this.constructor.name,
        type: 'networkError',
        code: 'streamLoadingError',
        message: this.defaultErrorMessage,
        description: message,
        fatal: true,
      };
      if (this.isInited) {
        this.player.emit('mediaError', error);
      } else {
        this.onError(error);
      }
    });
  };

  set getStream(value) {
    this._getStream = utils.decorateGetStream(this.player, value);
  }

  get getStream() {
    return this._getStream;
  }

  set getVirtualStream(value) {
    this._getVirtualStream = utils.decorateGetStream(this.player, value);
  }

  get getVirtualStream() {
    return this._getVirtualStream;
  }

  getStreamFromData = (data) => {
    let error;
    if (typeof data === 'string') {
      if (data.indexOf('<config>') === -1) {
        try {
          data = JSON.parse(data);
        } catch (e) {
          error = {
            name: this.constructor.name,
            type: 'parsingError',
            code: 'parsingError',
            message: this.defaultErrorMessage,
            description: e.message,
            fatal: true,
          };
          if (this.isInited) {
            this.player.emit('mediaError', error);
          } else {
            this.onError(error);
          }
          return null;
        }
      } else {
        data = this.binfoConverter.convert(data);
      }
    }

    if (utils.checkStreamIsValid(data)) {
      // adopt to legacy codebase
      data = {
        result: 'ok',
        data,
      };
    }

    if (data.result !== 'ok' || !data.data) {
      error = {
        name: this.constructor.name,
        type: 'streamError',
        code: 'responseError',
        message: this.defaultErrorMessage,
        description: data.error ? data.error.toString() : '',
        fatal: true,
      };

      // if (this.isInited) {
      //   this.player.emit('mediaError', error);
      // } else {
      this.onError(error);
      // }
      return null;
    }

    let stream = data.data;
    const ignoreSrc = this.player.get('ignoreSrc');

    this.config.drm = this.player.config.drm = Boolean(stream.drm_type);

    // MHP-1121
    var channel = this.player.get('epg:currentChannel');
    if (
      channel &&
      channel.type === 'vodChannel' &&
      stream.content_type === 'trailer'
    ) {
      stream.content_type = 'm_ch_vod';
    }

    //TODO: Выпилить как только CMS выльет share для desktop (MHP-1144)
    if (!this.player.get('isMobile')) {
      this.player.set('skin:showShareButton', Boolean(stream.share_link));
    }

    if (this.config.drm && this.config.supportedDrmType === 'fairplay') {
      stream.src = stream.src.replace(/\/a\/\d+/, '');
      const {
        config: {
          device: { os },
        },
      } = this;

      if (stream.drm_type === 'fairplay') {
        const { major, minor } = os;
        if (major > 10 || (major === 10 && minor >= 14)) {
          stream.bitrates = []; // MHP-597
        }
      }
    }

    stream = utils.modifyStreamForRequiredSubs(stream);

    if (this.config.drm && this.config.supportedDrmType) {
      if (!stream.drm_type || stream.drm_type === '') {
        stream.drm_type = this.config.supportedDrmType;
      }
    }

    if (
      stream.is_tv &&
      this.config.userAgent.browser.name === 'Safari' &&
      Number(this.config.userAgent.browser.major) < 10
    ) {
      stream.bitrates = [];
    }

    stream.src = utils.replaceProtocol(stream.src);

    if (!stream.src || stream.src.length < 5 || ignoreSrc) {
      error = {
        name: this.constructor.name,
        type: 'streamError',
        code: 'srcIsNull',
        message: this.defaultErrorMessage,
        description: stream.forbidden,
        fatal: true,
      };

      //MHP-1621: Под заглушкой проигрывается реклама
      stream.audio_tracks = stream.bitrates = [];

      this.player.set('videoInfo', stream);
      this.player.set('ignoreSrc', false);
      // if (this.isInited) {
      //   this.player.emit('mediaError', error);
      // } else {
      this.onError(error);
      // }
      return null;
    }

    if (this.config.drm && !this.config.supportedDrmType) {
      error = {
        name: this.constructor.name,
        type: 'drmError',
        code: 'drmNotSupported',
        message: this.defaultErrorMessage,
        description: stream.forbidden,
        fatal: true,
      };

      this.player.set('videoInfo', stream);

      // if (this.isInited) {
      //   this.player.emit('mediaError', error);
      // } else {
      this.onError(error);
      // }
      return null;
    }

    return stream;
  };

  onReady = () => {
    const playerWrapper = this.addElement(
      'mggp-player-wrapper',
      this.config.container
    );
    this.videoContainer = this.addElement(
      'mggp-video-container',
      playerWrapper
    );
    this.config.videoContainer = this.videoContainer;

    if (!window.mggPlayersPool) {
      window.mggPlayersPool = {};
    }

    window.mggPlayersPool[`${this.config.container.id}_player`] = this.player;

    playerWrapper.style.outline = 'none';
    playerWrapper.style.backgroundColor = 'black';

    if (this.config.skin) {
      this.skinContainer = this.addElement(
        `${this.config.container.id}_skin`,
        playerWrapper,
        'div'
      );
      this.skinContainer.style.width = '100%';
      this.skinContainer.style.height = '100%';
      this.skinContainer.style.position = 'absolute';
      this.skinContainer.style.display = 'flex';

      if (
        this.config.userAgent.browser.name === 'Safari' ||
        this.config.userAgent.browser.name === 'Mobile Safari'
      ) {
        // https://jira.x2sy.com/browse/MHP-1193
        // https://stackoverflow.com/questions/15751012/css-transform-causes-flicker-in-safari-but-only-when-the-browser-is-2000px-w
        // This cause RMI-1947, try to disable and retest related issue
        // this.skinContainer.style.transformStyle = 'preserve-3d';
      }

      initSkin(this.player, this.skinContainer, () => {
        this.config.adContainer = document.querySelector('#mggp-ad-container');
        if (this.config.onReady && this.config.onReady !== this.onReady) {
          this.initPlayer();
          this.config.onReady();
        }
      });
    }

    utils.checkIsIframe(this.player);
    utils.addContentBlockListener(this.player);
    utils.checkPlayerContainerDimension(this.config, this.config.container);
    checkGDPR(this.config);
  };

  stop = () => {
    if (this.isInited) {
      this.player.stop();
    }
    clearTimeout(this.rebinfoTimeout);
    this.stopped = true;
  };

  onError = (message) => {
    this.stop();
    this.player.emit('ga:customEvent', [
      message.type,
      message.code,
      this.config.href,
    ]);
    this.player.emit('player:playerError', message);
    if (this.config.onError && this.config.onError !== this.onError) {
      this.config.onError(message);
      // eslint-disable-next-line no-console
      console.log(message);
    }
  };

  init = (config) => {
    this.storage = new UserSettingsStorage(config.userID, config.userProfileID);
    this.config = makeConfig(this.player);
    this.config.storage = this.storage;
    this.moduleManager = new ModuleManager(this.config);
    this.config.playerOptions = this.config.checkDefaultOptions();

    const tempDebug = this.config.debug;
    utils.merge(config, this.config);

    this.moduleManager.initModules(config.type);
    this.config.logger = tempDebug;
    this.videoURL = this.config.videoURL;
    this.player.config = this.config;
    this.config.singleVideoInstance = true;
    this.neededScripts = [];

    // autoplay & intersection check
    const autoplayPromise = checkBrowserAutoplay(this.config);
    const intersectionPromise = utils.subscribeOnIntersectionObserver(
      this.player
    );
    Promise.all([autoplayPromise, intersectionPromise]).then(
      ([autoplayAbility]) => {
        // Promise.all returned values from every child promise in argument array in the same order as promises have passed
        const { muted, autoPlay, mutedAutoplay } = this.config; // so in this case es6 array destructing used
        const { defaultMuted } = this.config.checkDefaultOptions();

        // eslint-disable-next-line no-console
        console.log(
          `Config autoPlay ${autoPlay}, config.muted ${muted}, user muted ${defaultMuted}, browser autoplay ${autoplayAbility}`
        );

        if (
          autoplayAbility === BROWSER_AUTOPLAY_ABILITY.WHEN_MUTED &&
          (muted || defaultMuted || (autoPlay && mutedAutoplay))
        ) {
          //MHP-1584

          this.browserAutoplay = true;

          const muteVideos = () => {
            const videos = this.player.get('videoInstances');
            if (videos) {
              this.player.off('property:videoInstances', muteVideos);
              utils.toggleMutedOnVideos(videos, true, this.player);
            } else {
              this.player.on('property:videoInstances', muteVideos);
            }
          };
          muteVideos();
          this.config.container.addEventListener(
            'mousedown',
            this.onPlayerContainerClick,
            true
          );
          this.player.set(
            'browserAutoplayState',
            BROWSER_AUTOPLAY_STATE.AVAILABLE
          );
          if (this.videoInfoDeferredData) {
            this.onVideoInfo(this.videoInfoDeferredData);
            this.videoInfoDeferredData = null;
          }
        } else if (
          autoplayAbility !== BROWSER_AUTOPLAY_ABILITY.ALWAYS ||
          this.config.autoPlay === false
        ) {
          this.browserAutoplay = false;
          this.player.set(
            'browserAutoplayState',
            BROWSER_AUTOPLAY_STATE.NEED_TO_INIT_VIDEOS
          );
          this.config.container.addEventListener(
            'mousedown',
            this.onPlayerContainerClick,
            true
          );
        } else {
          // video can play with autoplay
          this.browserAutoplay = true;
          this.videosInited = true;
          this.player.set(
            'browserAutoplayState',
            BROWSER_AUTOPLAY_STATE.AVAILABLE
          );
          if (this.videoInfoDeferredData) {
            this.onVideoInfo(this.videoInfoDeferredData);
            this.videoInfoDeferredData = null;
          }
        }
      }
    );

    if (this.config.loadExternalLocalization) {
      if (this.config.localizationUrl) {
        this.config.localization.load(this.config.localizationUrl);
      } else {
        this.config.localization.loadDefault(this.config.lang);
      }
    }

    if (this.moduleManager.needChromecastScript()) {
      this.neededScripts.push(
        _.get(this.config.links, "libs['chromecast.js']", '')
      );
    }

    if (this.moduleManager.needPahoLib()) {
      this.neededScripts.push(_.get(this.config.links, 'libs.paho', ''));
    }

    this.neededScripts.push(_.get(this.config.links, 'libs.pal-sdk', ''));

    this.loadScripts(this.onReady);

    if (typeof this.config.i18n === 'function') {
      this.defaultErrorMessage =
        this.config.i18n('video_playback_trouble_cause_standard') ||
        this.defaultErrorMessage;
    }

    const {
      userAgent: { device },
    } = this.config;

    if (device.type === 'mobile' || device.type === 'tablet') {
      this.player.set('isMobile', true);
    }

    utils.processDeepTrack(this.player);

    if (!device.type && !window.MediaSource) {
      // id desktop cannot playing HLS/DASH
      const error = {
        name: this.constructor.name,
        type: 'mediaError',
        code: 'browserNotSupported',
        message: this.defaultErrorMessage,
        description: '',
        fatal: true,
      };
      setTimeout(this.onError, 50, error);
    }

    this.player.registerProperty('smallMode', false, true);

    return this.player;
  };

  load = (url, catchupTime, silentMode) => {
    //MHP-2009: Todo
    //Нужно оставить совместимость на время, пока все не перейдут на player.load(url, options), думаю через пару лет можно удалить ;)
    let options = {};
    if (catchupTime && typeof catchupTime === 'object') {
      options = catchupTime;
    }

    //MHP-2086
    clearTimeout(this.rebinfoTimeout);

    this.stopped = false;
    this.errorRetrycount = 0;
    this.errorRetryTimeout = 0;
    this.catchupTime = options['catchupTime'] || catchupTime;

    this.config.videoURL = url;
    this.videoURL = url;

    this.player.set('loadCalled', true);

    //MHP-1379: При silentMode мы должны безаговорочно стартовать без звука, при
    // этом не влияя на куку, после след вызова без флага мы должны вернуть
    // громкость с учетом куки. Прошлая реализация работала не корректно (
    const silent = Boolean(options['silentMode'] || silentMode);
    const mChTime = parseInt(options['mChTimeStart']);

    let interactiveChannelStartTime;

    if (this.catchupTime > 0) {
      interactiveChannelStartTime = this.catchupTime;
    } else if (mChTime > 0) {
      this.player.set('interactiveChannel:startTime', mChTime);
      interactiveChannelStartTime = mChTime;
    }

    if (interactiveChannelStartTime) {
      this.player.set(
        'epg2:interactiveChannelStartTime',
        interactiveChannelStartTime
      );
    }

    const defaultMuted = this.config.checkDefaultOptions().defaultMuted;
    if (silent && !this.videosInited) {
      this.videosInited = this.browserAutoplay = true;
    }

    if (this.player.get('player:silentMode') && !silent) {
      this.player.set('muted', Boolean(defaultMuted));
    }

    if (this.player.get('state') === 1) this.player.stop();

    this.player.set('player:silentMode', silent);

    if (this.catchupTime > 0) {
      this.getVirtualStream(this.videoURL, this.catchupTime, this.onVideoInfo);
      return;
    }

    if (typeof this.videoURL === 'number' && this.config.is_embed) {
      const streamUrl = `http://embed.megogo.net/aprx/stream?lang=${this.config.lang}&video_id=${this.videoURL}`;
      this.getStream(streamUrl, this.onVideoInfo);
    } else {
      this.getStream(this.videoURL, this.onVideoInfo);
    }
  };

  addElement = (id, parent, type) => {
    const element = document.createElement(type || 'div');
    if (id) {
      element.id = id;
    }
    parent.appendChild(element);

    return element;
  };

  loadScripts = (callback) => {
    let scriptsLoaded = 0;
    const onLoad = () => {
      if (++scriptsLoaded >= this.neededScripts.length) {
        callback();
      }
    };

    if (this.neededScripts.length > 0) {
      for (let i = 0, l = this.neededScripts.length; i < l; ++i) {
        const url = this.neededScripts[i];
        const isCSS = url.indexOf('.css') !== -1;
        let elem;

        if (isCSS) {
          elem = document.createElement('link');
          elem.href = url;
          elem.type = 'text/css';
          elem.rel = 'stylesheet';
        } else {
          elem = document.createElement('script');
          elem.src = url;
        }

        if (document.documentElement) {
          document.documentElement.appendChild(elem);
        }
        elem.onload = onLoad;
        elem.onerror = onLoad;
      }
    } else {
      // call callback on next js tick
      setTimeout(callback, 0);
    }
  };

  initPlayer = () => {
    const dataWs = this.config.getUserDataWS();
    this.config.deviceID = this.player.config.deviceID = dataWs
      ? dataWs.deviceId || dataWs.userId
      : '';
    if (this.config.deviceID && !this.config.userID) {
      this.config.userID = this.config.deviceID;
    }

    const config = {};
    config.autoRecoverError = true;
    config.forceKeyFrameOnDiscontinuity = false;
    config.abrEwmaFastVoD = 6;
    config.getStreamFromData = this.getStreamFromData;
    config.getDefaultLoader = this.getDefaultLoader;
    config.getStream = this.getStream;
    config.getVirtualStream = this.getVirtualStream;

    utils.merge(this.config, config);
    this.config.playerOptions = utils.checkVolume(
      this.player,
      this.config.playerOptions
    );

    const {
      userAgent: { device },
    } = this.config;

    device.model = device.model || '';
    device.type = device.type || '';
    device.vendor = device.vendor || '';

    // this.stream.src = this.config.protocol + this.stream.src.substr(this.stream.src.indexOf('//')); // need for https

    if (device.type === 'mobile' || device.type === 'tablet') {
      // initialize dirty tricks for mobile devices
      // if (os.name.toLowerCase().indexOf('ios') !== -1) {
      //   new MobileUtils(this.player, this.browserAutoplay); // eslint-disable-line
      // } // TODO need fix wstat fullscreen mode
    } else {
      this.player.set('isMobile', false);
    }

    this.player.on('property:autoplay', listeners.onAutoplayChanged);
    this.player.on('property:quality', listeners.onQualityChange);
    this.player.on('property:audioTrack', listeners.onAudioTrackChange);
    this.player.on('skin:audioButtonClick', listeners.onAudioButtonClick);
    this.player.on('preference:subtitles', listeners.onSubtitlesChange);
    this.player.on('property:volume', listeners.onVolumeChange);
    this.player.on('property:muted', listeners.onMutedChange);
    this.player.on('ad:external:adComplete', listeners.onAdComplete);
    this.player.on('changeVideo', listeners.onChangeVideo);
    this.player.on('property:state', listeners.onPlayerStateChange);
    this.player.on('player:completed', listeners.onPlayerComplete);
    this.player.on('player:messageMode', listeners.onMessageModeChanged);
    this.player.on('hideSkins', listeners.onHideSkins);
    this.player.on('showSkins', listeners.onShowSkins);
    this.player.on('adblock:detected', listeners.onAdBlockDetected);
    this.player.on('player:mediaError', listeners.onMediaError);
    this.player.on('mediaError', listeners.onMediaError);
    this.player.on('player:contentBlocked', listeners.onContentBlockDetected);
    this.player.on('contentBlocked', listeners.onContentBlockDetected);
    this.player.on('ad:imhoDetected', listeners.checkWeboramaDPM);
    this.player.on('ad:internal:*', listeners.onAdInternalEvent);
    this.player.on(
      `property:${AD_GLOBAL_TIMEOUTS}`,
      listeners.onAdGlobalTimeoutsChanged
    );
    this.player.on('epg2:channelClick', listeners.onEPG2ChannelClick);

    this.player.registerProperty(
      'vendor:dismissSeekToLiveAfterPause',
      false,
      true
    );
    if (!this.player.get('isMobile')) {
      this.player.registerProperty('player:usePluginBuffering', true, true);
    }

    this.player.registerProperty('epg2:onboarding', null, true);
    this.player.registerProperty('player:silentMode', false, true);
    this.player.registerProperty('loadCalled', false, true);
    this.player.registerProperty('ignoreSrc', false, true);
    this.player.registerProperty('ad:needAdfoxIpdid', true, true);
    this.player.registerProperty(
      'interactive:enable',
      config.interactive.enable,
      true
    );
    this.player.set('interactive:enable', config.interactive.enable);

    this.player.init(
      'player',
      config.videoContainer,
      config,
      this.moduleManager.getModules()
    );
    utils.addDataAdtConsentIgnoreAttribute(this.player.get('videoInstances'));
    this.isInited = true;
  };

  rebinfo = (event) => {
    if (this.errorRetrycount >= 2 || this.stopped) {
      return false;
    }

    this.errorRetrycount++;
    const options = this.player.get('vendorOptions') || {};

    const qualityList = this.player.get('qualityList') || [];
    for (let i = 0; i < qualityList.length; i++) {
      const quality = qualityList[i];
      if (quality.bitrate === Number(this.player.get('quality'))) {
        if (quality.bitrate !== -1) {
          options.defaultQuality = quality.bitrate;
        }
      }
    }

    options.startSeconds = options.startSeconds || this.player.get('position');

    if (
      !('defaultAudioTrack' in options) &&
      this.player.get('audioTrack') !== 0
    ) {
      options.defaultAudioTrack = this.player.get('audioTrack');
    }

    this.player.emit('loader:rebinfo', this.errorRetrycount);

    this.rebinfoTimeout = setTimeout(() => {
      this.errorRetryTimeout += 2000;
      //this.player.stop();
      let networkStatusCode = event.data.networkStatusCode || null;
      let videoInfo = this.player.get('videoInfo');

      switch (networkStatusCode) {
        case 500:
        case 502:
          this.player.load(videoInfo.src, videoInfo, options);
          break;
        case 403:
        case 404:
        case null:
          this.getStream(utils.replaceProtocol(this.videoURL || ''), (data) => {
            const stream = this.getStreamFromData(data);
            if (stream) {
              if (videoInfo && videoInfo.video_id !== this.stream.video_id) {
                this.player.load(videoInfo.src, videoInfo, options); //MHP-2272 Test 2/4
                return;
              }

              this.stream = utils.modifyStreamForRequiredSubs(stream);
              if (videoInfo) {
                videoInfo.src = this.stream.src;
              } else {
                videoInfo = this.stream;
              }
              videoInfo.rebinfo = true;
              videoInfo.src = videoInfo.src.replace(/https?:/, '');
              this.player.stop();
              this.player.load(videoInfo.src, videoInfo, options);
            } else {
              this.player.stop();

              const error = event.data;
              error.stream = this.videoURL;
              error.description = 'Cannot load or parse stream';
              error.message = this.defaultErrorMessage;
              this.onError(error);
            }
          });
          break;
      }
    }, this.errorRetryTimeout);

    return true;
  };

  checkIsCatchUp = (stream) => {
    let preventFurtherExecution = false;

    const emitErrorIfNeed = (currentTime) => {
      let errorEmmited = false;
      if (stream.start_timestamp > currentTime) {
        // if catchup in future dispatch error
        preventFurtherExecution = true;
        errorEmmited = true;
        this.onError({
          name: this.constructor.name,
          type: 'streamError',
          code: 'catchupInFuture',
          message: this.defaultErrorMessage,
          description: stream,
          fatal: true,
        });
      } else {
        if (this.catchupTime && this.catchupTime > 0) {
          stream.play_start_time = this.catchupTime - stream.start_timestamp;
          this.catchupTime = 0;
        }
      }
      return errorEmmited;
    };

    if (stream && stream.src && stream.content_type === 'tv_dvr_catchup') {
      const timestamp = this.player.get('epg:currentTimestamp');
      if (!timestamp) {
        // waiting epg time
        const onTimestamp = (event) => {
          if (event.data) {
            this.player.off('property:epg:currentTimestamp', onTimestamp);
            window.clearTimeout(timeoutId);
            if (!emitErrorIfNeed(event.data)) {
              this.onVideoInfo(stream);
            }
          }
        };
        this.player.on('property:epg:currentTimestamp', onTimestamp);
        preventFurtherExecution = true;

        const timeoutId = window.setTimeout(() => {
          this.player.off('property:epg:currentTimestamp', onTimestamp);
          this.load(stream.video_id, null);
        }, 5000);
      } else {
        const error = emitErrorIfNeed(timestamp);
        if (
          !error &&
          stream &&
          stream.start_timestamp < timestamp &&
          stream.end_timestamp > timestamp
        ) {
          //MHP-2221 : drop user to live if this cathup in live
          if (stream.content_type.includes('tv_dvr')) {
            this.load(stream.video_id, null);
            preventFurtherExecution = true;
            this.catchupTime = 0;
          }
        }
      }
    }
    return preventFurtherExecution;
  };

  onVideoInfo = (data) => {
    if (this.browserAutoplay === undefined) {
      // when promise on video.play hasn't resolved yet & this.browserAutoplay is exactly undefined
      this.videoInfoDeferredData = data;
      return;
    }

    if (!this.browserAutoplay && !this.videosInited) {
      // when promise on video.play has resolved and autoplay is false
      this.videoInfoDeferredData = data;
      return;
    }

    this.deferredPlayerOptions = null;

    this.stream = this.getStreamFromData(data);
    const preventExecution = this.checkIsCatchUp(this.stream);

    if (!this.stream || preventExecution) {
      this.stream = undefined;
      return;
    }

    utils.merge(this.config, this.player.config);

    if (this.stream.start_session_url) {
      const newUserID = utils.getUserId(this.stream.start_session_url);
      if (newUserID) {
        this.config.userID = newUserID;
      }
    }

    if (this.stream.is_embed) {
      const urlParser = this.config.getURLParser(window.location.href);
      if (!('as' in urlParser.query)) {
        this.config.adSpeed = 105;
      }
      this.config.embed = true;

      const embedUserLogin = Cookie.get('embed_user_logined');
      if (embedUserLogin !== '1') {
        this.stream.start_session_url = '';
      }

      utils.initGA();
    }

    if (!this.config.showCloseButton) {
      this.stream.showCloseButton = this.config.checkCloseButton();
    }

    const advertData = _.get(this.stream, 'advert_data', '');
    if (typeof advertData === 'object') {
      this.stream.advert_data = JSON.stringify(advertData);
    }
    const zstop = _.get(this.stream, 'advert_data', '').indexOf('zstop') !== -1;
    const sizeInfo = this.player.get('sizeInfo') || {};
    if (zstop && sizeInfo.intersectionRatio === 0) {
      this.stream.is_autoplay = false;
    }

    let autoplay;
    let silentMode = this.player.get('player:silentMode');
    if (this.browserAutoplayResolved) {
      autoplay = true;
      this.browserAutoplayResolved = false;
    } else {
      autoplay = checkStreamAutoplay(
        this.browserAutoplay,
        this.config.autoPlay,
        this.stream.is_autoplay ?? true
      );
    }
    if (!this.config.autoplay || silentMode) {
      this.config.autoPlay = true; // use 'false' value just once, for first run
    }

    if (silentMode) {
      this.player.set('muted', true);
    }

    if (this.player.get('src') || this.player.get('videoInfo')) {
      this.player.stop();
    }

    if (autoplay) {
      this.player.load(
        this.stream.src,
        this.stream,
        this.config.checkDefaultOptions(this.stream)
      );
    } else {
      this.player.set('autoplay', false);
    }
  };
}

window.MggpLoader = MggpLoader;
